import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient  from "../../../services/apiClient";

//Fetch income Data for Canvas
export const fetchIncomeData = createAsyncThunk(
  'income/fetchIncomeData',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/canvas/${canvasId}/incomes?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

//Create Income
export const createIncome = createAsyncThunk(
  'income/createIncome',
  async({canvasId, incomeData}, {rejectWithValue}) => {
    try{
      const response = await apiClient.post(
        `/canvas/${canvasId}/incomes`,
        incomeData
      );
      return response.data;
    }catch(error){
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

//Update Income
export const updateIncome = createAsyncThunk(
  'income/UpdateIncome',
  async({canvasId, incomeId, updateData}, {rejectWithValue}) => {
    try{
      const response = await apiClient.patch(
        `/canvas/${canvasId}/incomes/${incomeId}`,
        updateData
      );
      return response.data;
    }catch(error){
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

//Delete Income
export const deleteIncome = createAsyncThunk(
  'income/deleteIncome',
  async({canvasId, incomeId}, {rejectWithValue}) => {
    try{
      await apiClient.delete(
        `/canvas/${canvasId}/incomes/${incomeId}`
      );
      return incomeId;
    }catch(error){
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload incomes from file
export const uploadIncomes = createAsyncThunk(
  'income/uploadIncomes',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      if (!(file instanceof File)) {
        throw new Error(`Expected File object but got ${typeof file}: ${JSON.stringify(file)}`);
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/canvas/${canvasId}/incomes/upload`,
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const processManualDeposit = createAsyncThunk(
  'income/processManualDeposit',
  async ({ canvasId, incomeId, depositData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasId}/incomes/${incomeId}/deposit`,
        depositData );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

const incomeSlice = createSlice({
    name:'income',
    initialState:{
        incomes:[],
        pagination: { total: 0, page: 1, pages: 1 },
        loading:false,
        error:null,
    },
    reducers:{
        resetAccountError: (state) => {
            state.error = null;
        },
    },
    extraReducers:(builder) => {
        builder
        .addCase(fetchIncomeData.pending, (state)=>{
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchIncomeData.fulfilled, (state, action) => {
          state.loading = false;
          state.incomes = action.payload.incomes;
          state.pagination = {
            total: action.payload.total,
            page: action.payload.page,
            pages: action.payload.pages,
          };
        })
        .addCase(fetchIncomeData.rejected, (state, action)=>{
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(createIncome.pending, (state)=>{
            state.loading = true
            state.error = null
        })
        .addCase(createIncome.fulfilled, (state, action)=>{
            state.loading = false;
            state.error = null;
            state.incomes.push(action.payload)
        })
        .addCase(createIncome.rejected, (state, action)=>{
            state.loading= false;
            state.error = action.payload;
        })
        .addCase(updateIncome.pending, (state)=>{
            state.loading = true;
            state.error = false;
        })
        .addCase(updateIncome.fulfilled, (state, action)=>{
            state.loading = false;
            const idx = state.incomes.findIndex(inc => inc._id === action.payload._id);
            if (idx !== -1) state.incomes[idx] = action.payload;
        })
        .addCase(updateIncome.rejected, (state, action)=>{
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(deleteIncome.pending, (state)=>{
            state.loading = true;
            state.error = false;
        })
        .addCase(deleteIncome.fulfilled, (state, action)=>{
            state.loading = false;
            state.incomes = state.incomes.filter(inc => inc._id !== action.payload);
        })
        .addCase(deleteIncome.rejected, (state, action)=>{
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadIncomes.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(uploadIncomes.fulfilled, (state, action) => {
          state.loading = false;
          // merge uploaded incomes
          state.incomes = [...state.incomes, ...(action.payload || [])];
        })
        .addCase(uploadIncomes.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        .addCase(processManualDeposit.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(processManualDeposit.fulfilled, (state, action) => {
          state.loading = false;
          const idx = state.incomes.findIndex(inc => inc._id === action.payload.incomeId);
          if (idx !== -1) {
            state.incomes[idx].lastDepositDate = action.payload.depositDate;
          }
        })
        .addCase(processManualDeposit.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
    }
})

export const { resetAccountError } = incomeSlice.actions;
export const selectIncomes = state => state.income.incomes;
export const selectIncomeLoading = state => state.income.loading;
export const selectIncomePagination = state => state.income.pagination;
export default incomeSlice.reducer